<template>
  <div class="weekly-detail" :style="inputVisible ? { paddingBottom: inputHeight + keyboardOffset + 12 + 'px' } : null">
    <template v-if="detail">
      <van-tabs
        v-if="sections.length > 0"
        v-model="activeTab"
        scrollspy
        sticky
        :ellipsis="false"
        color="#1989FA"
        line-width="28px"
        line-height="2px"
        title-active-color="#323233"
        title-inactive-color="#969799">
        <van-tab v-for="section in sections" :key="section.key" :title="section.title">
          <div class="section">
          <div class="card">
            <div class="card-title" :class="'bg-' + section.key">
              <img :src="section.icon" width="16" height="16" alt="" />
              <span>{{ section.title }}</span>
            </div>

            <!-- 班级报告：可能多条 -->
            <template v-if="section.key == 'class'">
              <div v-for="(note, i) in detail.class_notes" :key="i">
                <div class="divider" v-if="i > 0"></div>
                <div class="part-title">
                  <span class="decor"></span>
                  <span class="text">{{ note.title || $t('weeklyReport.class_notes') }}</span>
                </div>
                <div class="card-content rich-text" v-html="normalText(note.content)"></div>
              </div>
            </template>

            <!-- 媒体库 -->
            <div v-else-if="section.key == 'media'" class="media-list">
              <div class="media-item" v-for="item in detail.media" :key="item.id">
                <img v-if="item.type == 'photo'" :src="item.url" class="media-pic" alt="" @click="previewPhoto(item)" />
                <template v-else>
                  <video class="media-pic video" :src="item.url + '#t=0.1'" controls preload="metadata" playsinline webkit-playsinline></video>
                  <div class="video-actions">
                    <span class="action" v-if="schoolType != 'ds'" @click="showDownloadGuide">
                      <span class="iconfont iconfont-wenhao mr-4"></span>{{ $t('notice.Download_Videos') }}
                    </span>
                    <span class="action" @click="showVideoLink(item.url)">
                      <span class="iconfont iconfont-copy mr-4"></span>{{ $t('notice.Video_Link') }}
                    </span>
                  </div>
                </template>
                <div class="media-text" v-if="item.content">{{ item.content }}</div>
              </div>
            </div>

            <div v-else class="card-content rich-text" v-html="normalText(section.content)"></div>
          </div>
          </div>
        </van-tab>
      </van-tabs>

      <!-- 留言区位置标记：滚动到这里时请求留言（留言卡片无数据时隐藏，不能作为监听目标） -->
      <div ref="commentAnchor"></div>
      <!-- 留言 -->
      <div class="card comment-card" v-if="comments.length > 0">
        <div class="card-title plain">
          <span>{{ $t('weeklyReport.comments') }}</span>
        </div>
        <div class="comment-body">
          <div class="comment-item" v-for="item in comments" :key="item.id">
            <div class="comment-user">
              <van-image round width="28px" height="28px" :src="item.avatar" />
              <span class="name">{{ item.name }}</span>
              <span class="time">{{ timeAgo(item.created_at) }}</span>
            </div>
            <div class="comment-content">{{ item.content }}</div>
            <div class="comment-from" v-if="item.identity">{{ $t('weeklyReport.from_' + item.identity) }}</div>
          </div>
        </div>
      </div>
    </template>

    <!-- 底部固定留言入口 -->
    <div class="comment-bar" v-if="detail" v-show="!inputVisible">
      <div class="comment-btn" @click="openInput">
        <img src="@/assets/weeklyReport/edit.svg" width="16" height="16" alt="" />
        <span>{{ $t('weeklyReport.comment_placeholder') }}</span>
      </div>
    </div>

    <!-- 留言输入框 -->
    <div class="input-box" ref="inputBox" v-show="inputVisible" :style="{ bottom: keyboardOffset + 'px' }">
      <div class="input-panel">
        <div class="tip"><van-icon name="info-o" /> {{ $t('global.comments_teacher') }}</div>
        <van-field
          ref="input"
          v-model="content"
          type="textarea"
          rows="4"
          maxlength="5000"
          class="input-field"
          :placeholder="$t('global.please_input')" />
        <div class="input-actions">
          <van-button round size="small" @click="closeInput">{{ $t('global.cancel') }}</van-button>
          <van-button round size="small" type="info" :disabled="!content.trim()" :loading="submitting" @click="submit">
            {{ $t('global.submit') }}
          </van-button>
        </div>
      </div>
    </div>

    <van-dialog v-model="qrcodeShow" :title="$t('notice.Video_Link')" :show-cancel-button="false" :show-confirm-button="false">
      <div class="qrcode-link">
        <div class="color6 font12 text-center mb-15">{{ $t('notice.download_or_QR') }}</div>
        <vue-qr :logoSrc="logoUrl" :text="currentLink" :logoScale="0.2" :logoMargin="2"
          :logoCornerRadius="2" :size="144" :margin="0" :correntLevel="1" />
        <div class="copy-link">{{ currentLink }}</div>
      </div>
      <van-row type="flex" justify="center" class="button-copy">
        <van-col span="12" @click="qrcodeShow = false">{{ $t('global.close') }}</van-col>
        <van-col span="12" class="btn-text" @click="copyLink" v-html="copyText"></van-col>
      </van-row>
    </van-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import { Tab, Tabs, Field, Button, Image as VanImage, Dialog, Toast, ImagePreview, Row, Col } from 'vant'
import vueQr from 'vue-qr'
import storage from 'store'
import store from '../store'
import { APP_LANGUAGE } from '@/store/mutation-types'
import { setDocumentTitle } from '@/plugins/domUtil'
import { weeklyReportDetail, weeklyReportComments, weeklyReportCommentSave } from '@/api/weeklyReport'

Vue.use(Tab)
Vue.use(Tabs)
Vue.use(Field)
Vue.use(Button)
Vue.use(VanImage)
Vue.use(Dialog)
Vue.use(Toast)
Vue.use(Row)
Vue.use(Col)

const ICONS = {
  important: require('@/assets/weeklyReport/remind.svg'),
  school: require('@/assets/weeklyReport/school-news.svg'),
  class: require('@/assets/weeklyReport/class-notes.svg'),
  child: require('@/assets/weeklyReport/child-report.svg'),
  media: require('@/assets/weeklyReport/media.svg'),
}

// 旧版 normalText：去掉编辑器产生的空段落和字号、字体、边距样式
const WORD_P = '<p 50="" 59="" ms="" yahei="" arial="" helvetica="" sans-serif="" vertical-align:="" baseline="" color:="" rgb="" style="'
const EMPTY_PATTERNS = [
  '<p><strong style="font-size: 10pt;"><br/></strong></p>',
  WORD_P + 'font-weight: bold; font-size: 13.3333px; margin: 0px 0px 1em; padding: 0px; border: 0px; font-variant-numeric: inherit; font-stretch: inherit; line-height: inherit;"><br /></p>',
  '<p inherit="" baseline="" 13px="" sans-serif="" helvetica="" arial="" yahei="" ms="" style="font-weight: bold; font-size: 13.3333px; margin: 0px 0px 1em; padding: 0px; border: 0px currentcolor; color: rgb(68, 59, 50);"><br /></p>',
  WORD_P + 'margin: 0px 0px 1em; padding: 0px; border: 0px; font-variant-numeric: inherit; font-stretch: inherit; font-size: 13.3333px; line-height: inherit;"><strong><br /></strong></p>',
  WORD_P + 'font-weight: bold; font-size: 13.3333px; margin: 0px 0px 1em; padding: 0px; border: 0px; font-variant-numeric: inherit; font-stretch: inherit; line-height: inherit;"><span style="font-size: 13.3333px;"><br /></span></p>',
  '<div><br /></div>',
  '<p>&nbsp;</p>',
  '<p><br/><br/></p>',
  '<p><strong>&nbsp;</strong></p>',
]

export default {
  name: 'WeeklyReportDetail',
  components: { vueQr },
  data () {
    return {
      childId: this.$route.query.childid,
      yid: this.$route.query.yid,
      week: this.$route.query.week,
      detail: null,
      activeTab: 0,
      comments: [],
      commentsLoaded: false,
      observer: null,
      inputVisible: false,
      inputHeight: 0,
      keyboardOffset: 0,
      content: '',
      submitting: false,
      schoolType: store.getters.wxState || 'ds',
      qrcodeShow: false,
      currentLink: '',
      copyText: '',
      logoUrl: '',
    }
  },
  computed: {
    sections () {
      const d = this.detail
      if (!d) return []
      const list = []
      if (d.important) list.push({ key: 'important', title: this.$t('weeklyReport.key_reminders'), content: d.important })
      if (d.school_news) list.push({ key: 'school', title: this.$t('weeklyReport.school_news'), content: d.school_news })
      if (d.class_notes.length > 0) list.push({ key: 'class', title: this.$t('weeklyReport.class_notes') })
      if (d.child_report) list.push({ key: 'child', title: this.$t('weeklyReport.child_report'), content: d.child_report })
      if (d.media.length > 0) list.push({ key: 'media', title: this.$t('weeklyReport.class_media') })
      return list.map(item => ({ ...item, icon: ICONS[item.key] }))
    },
  },
  created () {
    setDocumentTitle('Week ' + String(this.week).padStart(2, '0'))
    this.fetchData()
  },
  beforeDestroy () {
    if (this.observer) this.observer.disconnect()
    this.unwatchKeyboard()
  },
  methods: {
    params () {
      return { child_id: this.childId, yid: this.yid, week: this.week }
    },
    fetchData () {
      weeklyReportDetail(this.params()).then(res => {
        if (res.code != 0) return
        this.detail = res.data
        this.$nextTick(() => {
          this.observeComments()
          // 从“留言回复”微信通知进入时自动滚动到留言区
          if (this.$route.query.anchor === 'comments') this.scrollToComments()
        })
      })
    },
    scrollToComments () {
      const el = this.$refs.commentAnchor
      if (!el) return
      // 减去吸顶 Tab 栏高度和卡片间距，避免留言卡片被 Tab 栏遮住
      const offset = (this.sections.length > 0 ? 44 : 0) + 12
      window.scrollTo(0, el.getBoundingClientRect().top + window.pageYOffset - offset)
    },
    // 滑动到留言区时才请求留言（接口会标记已读）
    observeComments () {
      const el = this.$refs.commentAnchor
      if (!el) return
      if (!('IntersectionObserver' in window)) {
        this.fetchComments()
        return
      }
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect()
          this.observer = null
          this.fetchComments()
        }
      })
      this.observer.observe(el)
    },
    fetchComments () {
      weeklyReportComments(this.params()).then(res => {
        if (res.code != 0) return
        this.comments = res.data
        // 留言卡片渲染后高度变化，首次加载时再定位一次
        if (!this.commentsLoaded && this.$route.query.anchor === 'comments') {
          this.$nextTick(this.scrollToComments)
        }
        this.commentsLoaded = true
      })
    },
    openInput () {
      this.inputVisible = true
      // v-show 要等下一轮渲染才生效，先同步显示面板，否则对 display:none 的输入框 focus 无效
      this.$refs.inputBox.style.display = ''
      // 需在点击的同步调用栈中 focus，微信 webview 才会弹出键盘
      this.$refs.input.focus()
      this.inputHeight = this.$refs.inputBox.offsetHeight
      this.watchKeyboard()
      // 有留言时滚动到最底部，让留言列表显示在输入框上方；没有留言时停留在原位置
      if (this.comments.length > 0) {
        this.$nextTick(this.scrollToBottom)
        // 键盘弹出会改变可视区域高度，弹出后再滚一次
        setTimeout(this.scrollToBottom, 300)
      }
    },
    scrollToBottom () {
      if (!this.inputVisible) return
      const top = document.documentElement.scrollHeight
      window.scrollTo(0, top)
      document.documentElement.scrollTop = top
      document.body.scrollTop = top
    },
    // iOS 键盘弹出时布局视口不缩小，fixed 在底部的输入框会被键盘遮挡，
    // 用 visualViewport 计算键盘占用的高度，把输入框抬到键盘上方（Android 上该值为 0）
    updateKeyboardOffset () {
      const vv = window.visualViewport
      if (!vv) return
      this.keyboardOffset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
    },
    watchKeyboard () {
      const vv = window.visualViewport
      if (!vv) return
      vv.addEventListener('resize', this.updateKeyboardOffset)
      vv.addEventListener('scroll', this.updateKeyboardOffset)
    },
    unwatchKeyboard () {
      const vv = window.visualViewport
      if (!vv) return
      vv.removeEventListener('resize', this.updateKeyboardOffset)
      vv.removeEventListener('scroll', this.updateKeyboardOffset)
    },
    closeInput () {
      this.inputVisible = false
      this.$refs.input.blur()
      this.unwatchKeyboard()
      this.keyboardOffset = 0
    },
    submit () {
      const content = this.content.trim()
      if (!content || this.submitting) return
      this.submitting = true
      weeklyReportCommentSave({ ...this.params(), content }).then(res => {
        this.submitting = false
        if (res.code != 0) return
        Toast.success(this.$t('weeklyReport.submit_success'))
        this.content = ''
        this.closeInput()
        this.fetchComments()
      }).catch(() => {
        this.submitting = false
      })
    },
    normalText (html) {
      if (!html) return ''
      let s = html
      EMPTY_PATTERNS.forEach(p => { s = s.split(p).join('') })
      return s
        .replace(/<pre>/g, '<p>')
        .replace(/<\/pre>/g, '</p>')
        .replace(/font-size/gi, '')
        .replace(/font-family/gi, '')
        .replace(/margin/gi, '')
        .replace(/padding/gi, '')
    },
    // 预览大图：w600 / ww600 样式换成 w2000（与旧版一致）
    previewPhoto (item) {
      const photos = this.detail.media.filter(m => m.type == 'photo')
      const toLarge = url => url.replace('ww600', 'w2000').replace('w600', 'w2000')
      ImagePreview({
        images: photos.map(m => toLarge(m.url)),
        startPosition: photos.findIndex(m => m.id == item.id),
      })
    },
    showDownloadGuide () {
      const lang = storage.get(APP_LANGUAGE) || 'zh'
      const img = lang == 'zh'
        ? 'https://m2.files.ivykids.cn/cloud01-file-8025768FhqVad_IuSH7i0eD4nb1Wf7d02Kd.jpg'
        : 'https://m2.files.ivykids.cn/cloud01-file-8025768Fhgwc-9-2tE_02XD8mOt9LKEU9DV.jpg'
      ImagePreview({ images: [img] })
    },
    showVideoLink (url) {
      this.logoUrl = this.schoolType == 'ds' ? require('@/assets/qr_ds_logo.png') : require('@/assets/qr_ivy_logo.png')
      this.copyText = this.$t('notice.Copy_Link')
      this.currentLink = url
      this.qrcodeShow = true
    },
    copyLink () {
      const input = document.createElement('input')
      input.value = this.currentLink
      document.body.appendChild(input)
      input.select()
      document.execCommand('Copy')
      document.body.removeChild(input)
      this.copyText = '<span class="iconfont iconfont-success"></span> ' + this.$t('notice.Copied')
      setTimeout(() => { this.qrcodeShow = false }, 1000)
    },
    timeAgo (timestamp) {
      const diff = Math.floor(Date.now() / 1000) - timestamp
      if (diff < 60) return this.$t('weeklyReport.just_now')
      if (diff < 3600) return this.$t('weeklyReport.minutes_ago', { n: Math.floor(diff / 60) })
      if (diff < 86400) return this.$t('weeklyReport.hours_ago', { n: Math.floor(diff / 3600) })
      if (diff < 30 * 86400) return this.$t('weeklyReport.days_ago', { n: Math.floor(diff / 86400) })
      const d = new Date(timestamp * 1000)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
  },
}
</script>

<style lang="less" scoped>
.weekly-detail {
  // border-box：底部留白计入 100vh 内，内容不足一屏时不出现滚动条
  box-sizing: border-box;
  min-height: 100vh;
  // 为底部固定留言栏留出空间：12px 间距 + 栏高 52px + 安全区
  padding-bottom: calc(64px + env(safe-area-inset-bottom));
  background: #F5F6F8;
}
// 用 padding 而不是 margin 留出间距：点击 Tab 时 vant 滚动到 tab 内容区顶部，
// margin 会与内容区发生外边距合并，导致卡片紧贴 Tab 栏
.section {
  padding: 12px 12px 0;
}
.card {
  background: #FFFFFF;
  border-radius: 12px;
  overflow: hidden;
}
.comment-card {
  margin: 12px 12px 0;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  font-size: 16px;
  font-weight: 500;
  color: #323233;
  background: linear-gradient(to bottom, var(--from), #FFFFFF);
  &.plain { background: #FFFFFF; }
  &.bg-important { --from: #FFEFED; }
  &.bg-school { --from: #FFF2DF; }
  &.bg-class { --from: #DFF1FF; }
  &.bg-child { --from: #DCF6DA; }
  &.bg-media { --from: #E6E4FF; }
}
.card-content {
  padding: 0 16px 16px;
  font-size: 14px;
  color: #323233;
  word-break: break-word;
  /deep/ a {
    color: #1989FA;
  }
}
.part-title {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 16px 16px 0;
  font-size: 16px;
  font-weight: 500;
  color: #323233;
  .decor {
    flex-shrink: 0;
    width: 6px;
    height: 16px;
    margin-top: 3px;
    background: #1989FA;
    border-radius: 0 99px 99px 0;
  }
  .text {
    flex: 1;
    word-break: break-word;
  }
}
.divider {
  margin: 0 16px;
  height: 0.5px;
  background: #DCDEE0;
}
.media-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px;
}
.media-item {
  background: #F5F6F8;
  border-radius: 12px;
  overflow: hidden;
  .media-pic {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
    &.video {
      height: 196px;
      background: #000000;
      object-fit: contain;
    }
  }
  .video-actions {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    padding: 12px;
    font-size: 12px;
    line-height: 18px;
    color: #646566;
    .action {
      display: inline-flex;
      align-items: center;
    }
  }
  // 按钮下方已有 12px 间距，说明文字不再重复留上边距
  .video-actions + .media-text {
    padding-top: 0;
  }
  .media-text {
    padding: 12px;
    font-size: 12px;
    color: #323233;
    word-break: break-word;
  }
}
.comment-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px;
}
.comment-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  background: #FFFFFF;
  border-top: 0.5px solid #EBEDF0;
}
.comment-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #F5F6F8;
  border-radius: 999px;
  font-size: 14px;
  line-height: 20px;
  color: #C8C9CC;
}
.comment-user {
  display: flex;
  align-items: center;
  gap: 8px;
  .name {
    flex: 1;
    padding-bottom: 6px;
    font-size: 12px;
    line-height: 18px;
    color: #646566;
  }
  .time {
    padding-bottom: 6px;
    font-size: 12px;
    line-height: 18px;
    color: #969799;
  }
}
.comment-content {
  padding-left: 36px;
  font-size: 14px;
  line-height: 20px;
  color: #323233;
  white-space: pre-wrap;
  word-break: break-word;
}
.comment-from {
  margin-top: 8px;
  padding-left: 36px;
  font-size: 12px;
  line-height: 18px;
  color: #969799;
}
.input-box {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  padding: 12px 8px;
  background: #F5F6F8;
  .input-panel {
    padding: 12px;
    background: #FFFFFF;
    border: 1px solid #1989FA;
    border-radius: 12px;
  }
  .tip {
    font-size: 12px;
    line-height: 17px;
    color: #646566;
  }
  .input-field {
    margin-top: 12px;
    padding: 8px 12px;
    background: #F5F6F8;
    border-radius: 8px;
  }
  .input-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 12px;
    .van-button {
      width: 60px;
    }
  }
}
.qrcode-link {
  padding: 4px 24px 24px;
  text-align: center;
  .copy-link {
    margin-top: 12px;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    word-break: break-all;
  }
}
.button-copy {
  height: 55px;
  line-height: 55px;
  text-align: center;
  border-top: 1px solid #EBEDF0;
  .btn-text {
    border-left: 1px solid #EBEDF0;
    color: #1989FA;
  }
}
</style>
